import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 产品概览页面容器
const OverviewContainer = styled('div', {
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 1rem',
  backgroundColor: 'var(--surface-color)'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  textAlign: 'center',
  margin: '2rem 0',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  textAlign: 'center',
  maxWidth: '800px',
  margin: '0 auto 3rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.6
});

// 产品卡片容器
const ProductGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '2rem',
  margin: '2rem 0'
});

// 产品卡片样式
const ProductCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backgroundColor: 'var(--card-bg-color)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)'
  }
});

// 产品图片样式
const ProductImage = styled('div', {
  height: '200px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat'
});

// 产品信息区块
const ProductInfo = styled('div', {
  padding: '1.5rem',
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

// 产品标题
const ProductTitle = styled('h2', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '0.75rem'
});

// 产品描述
const ProductDescription = styled('p', {
  color: 'var(--text-secondary)',
  lineHeight: 1.6,
  marginBottom: '1.5rem',
  flex: 1
});

// 产品链接按钮
const ProductLink = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  backgroundColor: 'var(--button-primary)',
  color: 'var(--button-primary-text)',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'var(--button-primary-hover)'
  }
});

/**
 * 产品概览页面组件
 * 展示所有产品类别，包括云挖矿、矿机托管、ASIC商店、NFT矿场和Kipa钱包
 */
const ProductsOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // 产品数据数组，包含每个产品类别的信息
  const products = [
    {
      id: 'cloud-mining',
      title: t('products.cloudMining.title'),
      description: t('products.cloudMining.overview'),
      imagePath: '/images/products/cloud-mining.jpg',
      path: '/products/cloud-mining'
    },
    {
      id: 'mining-hotel',
      title: t('products.miningHotel.title'),
      description: t('products.miningHotel.overview'),
      imagePath: '/images/products/mining-hotel.jpg',
      path: '/products/mining-hotel'
    },
    {
      id: 'asic-shop',
      title: t('products.asicShop.title'),
      description: t('products.asicShop.overview'),
      imagePath: '/images/products/asic-shop.jpg',
      path: '/products/asic-shop'
    },
    {
      id: 'nft-farm',
      title: t('products.nftFarm.title'),
      description: t('products.nftFarm.overview'),
      imagePath: '/images/products/nft-farm.jpg',
      path: '/products/nft-farm'
    },
    {
      id: 'kipa-wallet',
      title: t('products.kipaWallet.title'),
      description: t('products.kipaWallet.overview'),
      imagePath: '/images/products/kipa-wallet.jpg',
      path: '/products/kipa-wallet'
    }
  ];
  
  return (
    <OverviewContainer>
      <Title>{t('products.overview.title')}</Title>
      <Subtitle>{t('products.overview.subtitle')}</Subtitle>
      
      <ProductGrid>
        {products.map(product => (
          <ProductCard key={product.id}>
            <ProductImage css={{ backgroundImage: `url(${product.imagePath})` }} />
            <ProductInfo>
              <ProductTitle>{product.title}</ProductTitle>
              <ProductDescription>{product.description}</ProductDescription>
              <ProductLink to={product.path}>{t('common.learnMore')}</ProductLink>
            </ProductInfo>
          </ProductCard>
        ))}
      </ProductGrid>
    </OverviewContainer>
  );
};

export default ProductsOverview; 